import { create } from 'zustand';
import { supabase, isSupabaseConfigured } from '../lib/supabase';
import { callEdgeFunction } from '../utils/callEdge';
import type { 
  FinancialPlan, 
  FinancialScenario, 
  BusinessPlanUpload,
  SimulationParameters,
  FinancialObjectives,
  ComparisonView
} from '../types/businessPlan';

interface BusinessPlanState {
  currentPlan: FinancialPlan | null;
  scenarios: FinancialScenario[];
  comparisonView: ComparisonView | null;
  isLoading: boolean;
  isAnalyzing: boolean;
  isSimulating: boolean;
  error: string | null;
  
  // Actions
  uploadAndAnalyze: (projectId: string, upload: BusinessPlanUpload) => Promise<void>;
  runSimulation: (planId: string, parameters: SimulationParameters, name: string) => Promise<FinancialScenario>;
  saveScenario: (scenario: FinancialScenario) => Promise<void>;
  loadPlan: (projectId: string) => Promise<void>;
  loadScenarios: (planId: string) => Promise<void>;
  compareScenarios: (scenarioIds: string[]) => Promise<void>;
  deletePlan: (planId: string) => Promise<void>;
  deleteScenario: (scenarioId: string) => Promise<void>;
  
  // Utilities
  clearError: () => void;
  setCurrentPlan: (plan: FinancialPlan | null) => void;
  updatePlanKPIs: (planId: string, updatedKPIs: FinancialKPIs) => Promise<void>;
}

export const useBusinessPlanStore = create<BusinessPlanState>((set, get) => ({
  currentPlan: null,
  scenarios: [],
  comparisonView: null,
  isLoading: false,
  isAnalyzing: false,
  isSimulating: false,
  error: null,

  uploadAndAnalyze: async (projectId: string, upload: BusinessPlanUpload) => {
    set({ isAnalyzing: true, error: null });
    
    try {
      if (!isSupabaseConfigured()) {
        throw new Error('Supabase n\'est pas configuré. Veuillez configurer la connexion.');
      }

      // Prepare data for AI analysis
      let fileContent = '';
      
      if (upload.file) {
        // Read file content
        if (upload.file.type.includes('csv')) {
          fileContent = await upload.file.text();
        } else if (upload.file.type.includes('excel') || upload.file.name.endsWith('.xlsx')) {
          // For Excel files, we'll need to convert to text or send the file directly
          fileContent = `Excel file: ${upload.file.name} (${upload.file.size} bytes)`;
        }
      } else if (upload.raw_text) {
        fileContent = upload.raw_text;
      }

      // Call AI analysis Edge Function
      const analysisResult = await callEdgeFunction('analyze-financial-plan', {
        project_id: projectId,
        file_content: fileContent,
        file_name: upload.file?.name,
        objectives: upload.objectives
      });

      // Save the financial plan to database
      const { data: plan, error: planError } = await supabase
        .from('financial_plans')
        .insert({
          project_id: projectId,
          name: `Business Plan v${Date.now()}`,
          version: 1,
          raw_data: {
            file_content: fileContent,
            file_name: upload.file?.name,
            upload_date: new Date().toISOString()
          },
          analysis_result: analysisResult,
          objectives: upload.objectives,
          created_by: (await supabase.auth.getUser()).data.user?.id
        })
        .select()
        .maybeSingle();

      if (planError) throw planError;
      if (!plan) throw new Error('Failed to create business plan');

      set({ currentPlan: plan, isAnalyzing: false });
      
      // Load scenarios for this plan
      await get().loadScenarios(plan.id);

    } catch (error: any) {
      set({ error: error.message, isAnalyzing: false });
      throw error;
    }
  },

  runSimulation: async (planId: string, parameters: SimulationParameters, name: string) => {
    set({ isSimulating: true, error: null });
    
    try {
      const { currentPlan } = get();
      if (!currentPlan) throw new Error('Aucun plan financier chargé');

      // Call simulation Edge Function
      const simulationResult = await callEdgeFunction('simulate-financial-scenario', {
        financial_plan_id: planId,
        parameters,
        base_analysis: currentPlan.analysis_result
      });

      // Create scenario object
      const scenario: FinancialScenario = {
        id: crypto.randomUUID(),
        financial_plan_id: planId,
        name,
        description: `Simulation avec ${parameters.ca_variation > 0 ? '+' : ''}${parameters.ca_variation}% CA, ${parameters.charges_fixes_variation > 0 ? '+' : ''}${parameters.charges_fixes_variation}% charges fixes`,
        parameters,
        results: simulationResult.kpis,
        created_at: new Date().toISOString()
      };

      // Save scenario to database
      const { error: scenarioError } = await supabase
        .from('financial_scenarios')
        .insert(scenario);

      if (scenarioError) throw scenarioError;

      // Update local state
      set(state => ({
        scenarios: [...state.scenarios, scenario],
        isSimulating: false
      }));

      return scenario;

    } catch (error: any) {
      set({ error: error.message, isSimulating: false });
      throw error;
    }
  },

  saveScenario: async (scenario: FinancialScenario) => {
    try {
      const { error } = await supabase
        .from('financial_scenarios')
        .upsert(scenario);

      if (error) throw error;

      // Update local state
      set(state => ({
        scenarios: state.scenarios.map(s => 
          s.id === scenario.id ? scenario : s
        )
      }));

    } catch (error: any) {
      set({ error: error.message });
      throw error;
    }
  },

  loadPlan: async (projectId: string) => {
    set({ isLoading: true, error: null });
    
    try {
      const { data: plan, error } = await supabase
        .from('financial_plans')
        .select('*')
        .eq('project_id', projectId)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) throw error;

      set({ currentPlan: plan, isLoading: false });

      if (plan) {
        await get().loadScenarios(plan.id);
      }

    } catch (error: any) {
      set({ error: error.message, isLoading: false });
    }
  },

  loadScenarios: async (planId: string) => {
    try {
      const { data: scenarios, error } = await supabase
        .from('financial_scenarios')
        .select('*')
        .eq('financial_plan_id', planId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      set({ scenarios: scenarios || [] });

    } catch (error: any) {
      set({ error: error.message });
    }
  },

  compareScenarios: async (scenarioIds: string[]) => {
    set({ isLoading: true, error: null });
    
    try {
      const { currentPlan, scenarios } = get();
      if (!currentPlan) throw new Error('Aucun plan financier chargé');

      const selectedScenarios = scenarios.filter(s => scenarioIds.includes(s.id));
      
      const comparisonView: ComparisonView = {
        scenarios: selectedScenarios,
        base_plan: currentPlan,
        comparison_metrics: ['ca_total', 'marge_nette', 'point_mort_mois', 'cashflow_cumule']
      };

      set({ comparisonView, isLoading: false });

    } catch (error: any) {
      set({ error: error.message, isLoading: false });
    }
  },

  deletePlan: async (planId: string) => {
    try {
      const { error } = await supabase
        .from('financial_plans')
        .delete()
        .eq('id', planId);

      if (error) throw error;

      set({ currentPlan: null, scenarios: [], comparisonView: null });

    } catch (error: any) {
      set({ error: error.message });
      throw error;
    }
  },

  deleteScenario: async (scenarioId: string) => {
    try {
      const { error } = await supabase
        .from('financial_scenarios')
        .delete()
        .eq('id', scenarioId);

      if (error) throw error;

      set(state => ({
        scenarios: state.scenarios.filter(s => s.id !== scenarioId)
      }));

    } catch (error: any) {
      set({ error: error.message });
      throw error;
    }
  },

  clearError: () => set({ error: null }),
  setCurrentPlan: (plan) => set({ currentPlan: plan }),

  updatePlanKPIs: async (planId: string, updatedKPIs: FinancialKPIs) => {
    try {
      const { currentPlan } = get();
      if (!currentPlan || currentPlan.id !== planId) return;

      const updatedAnalysis = {
        ...currentPlan.analysis_result,
        kpis: updatedKPIs
      };

      // Update in database
      const { error } = await supabase
        .from('financial_plans')
        .update({
          analysis_result: updatedAnalysis,
          updated_at: new Date().toISOString()
        })
        .eq('id', planId);

      if (error) throw error;

      // Update local state
      set({
        currentPlan: {
          ...currentPlan,
          analysis_result: updatedAnalysis
        }
      });

    } catch (error: any) {
      set({ error: error.message });
      throw error;
    }
  }
}));